import { getActiveTenantKey, getTenantScopedKey } from '../offline/tenant'
import { loadJSON, saveJSON } from './storage'
import { normalizeReportScope, type ReportScopeV1 } from './reportScope'

export type ReportVisibility = 'private' | 'team'
export type ReportDatasetKey = 'tasks' | 'inventory' | 'productionDatatable' | 'water' | 'lots' | 'feed' | 'feedEvents' | 'feedDailyTotals' | 'lotCostPerKg' | 'weightSampleIndividual' | 'lotGrowthPerformance' | 'mortalityRecording' | 'mortalityByCause' | 'lotProjection' | 'lotHatchery' | 'mortalityEvents' | 'deletedLots'

export type ReportFxConfigV1 = {
  targetCurrency: string
  asOf: string
}

export type ReportDefinitionV1 = {
  version: 1
  id: string
  tenantKey: string
  name: string
  datasetKey: ReportDatasetKey
  visibility: ReportVisibility
  columns: string[]
  scope?: ReportScopeV1 | null
  fx?: ReportFxConfigV1 | null
  createdAt: string
  updatedAt: string
}

type ReportsPayloadV1 = {
  version: 1
  items: ReportDefinitionV1[]
}

const REPORTS_STORAGE_KEY = 'reports:v1'

const reportsKey = (tenantKey = getActiveTenantKey()): string => (
  getTenantScopedKey(REPORTS_STORAGE_KEY, tenantKey)
)

const normalizeColumns = (columns: unknown): string[] => {
  if (!Array.isArray(columns)) return []
  return columns
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter(Boolean)
}

const isVisibility = (value: unknown): value is ReportVisibility => (
  value === 'private' || value === 'team'
)

const isDatasetKey = (value: unknown): value is ReportDatasetKey => (
  value === 'tasks'
  || value === 'inventory'
  || value === 'productionDatatable'
  || value === 'water'
  || value === 'lots'
  || value === 'feed'
  || value === 'feedEvents'
  || value === 'feedDailyTotals'
  || value === 'lotCostPerKg'
  || value === 'weightSampleIndividual'
  || value === 'lotGrowthPerformance'
  || value === 'mortalityRecording'
  || value === 'mortalityByCause'
  || value === 'lotProjection'
  || value === 'lotHatchery'
  || value === 'mortalityEvents'
  || value === 'deletedLots'
)

const normalizeReportFx = (value: unknown): ReportFxConfigV1 | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const targetCurrency = typeof record.targetCurrency === 'string'
    ? record.targetCurrency.trim().toUpperCase()
    : ''
  const asOf = typeof record.asOf === 'string' ? record.asOf.trim() : ''

  if (!/^[A-Z]{3}$/.test(targetCurrency)) return null
  if (!asOf) return null
  const parsedAsOf = new Date(asOf).getTime()
  if (!Number.isFinite(parsedAsOf)) return null

  return {
    targetCurrency,
    asOf: new Date(parsedAsOf).toISOString()
  }
}

const normalizeReport = (candidate: unknown, tenantKey: string): ReportDefinitionV1 | null => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null

  const record = candidate as Record<string, unknown>

  const idValue = record.id
  const nameValue = record.name
  const datasetKeyValue = record.datasetKey

  if (typeof idValue !== 'string' || idValue.trim().length === 0) return null
  if (typeof nameValue !== 'string' || nameValue.trim().length === 0) return null
  if (!isDatasetKey(datasetKeyValue)) return null

  const now = new Date().toISOString()
  const createdAt = typeof record.createdAt === 'string' ? record.createdAt : now
  const updatedAt = typeof record.updatedAt === 'string' ? record.updatedAt : now

  const visibility = isVisibility(record.visibility) ? record.visibility : 'private'
  const columns = normalizeColumns(record.columns)
  const scope = normalizeReportScope(record.scope)
  const fx = normalizeReportFx(record.fx)

  return {
    version: 1,
    id: idValue.trim(),
    tenantKey,
    name: nameValue.trim(),
    datasetKey: datasetKeyValue,
    visibility,
    columns,
    scope,
    fx,
    createdAt,
    updatedAt
  }
}

export const loadReports = (tenantKey = getActiveTenantKey()): ReportDefinitionV1[] => {
  const payload = loadJSON<ReportsPayloadV1>(reportsKey(tenantKey), { version: 1, items: [] })
  const normalized = (payload.items ?? [])
    .map((item) => normalizeReport(item, tenantKey))
    .filter((item): item is ReportDefinitionV1 => Boolean(item))

  return normalized.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

const persistReports = (reports: ReportDefinitionV1[], tenantKey = getActiveTenantKey()): void => {
  saveJSON<ReportsPayloadV1>(reportsKey(tenantKey), { version: 1, items: reports })
}

export const upsertReport = (report: ReportDefinitionV1, tenantKey = getActiveTenantKey()): ReportDefinitionV1[] => {
  const existing = loadReports(tenantKey)
  const next = existing.filter((item) => item.id !== report.id)
  next.unshift({ ...report, tenantKey, updatedAt: new Date().toISOString() })
  persistReports(next, tenantKey)
  return next
}

export const deleteReport = (id: string, tenantKey = getActiveTenantKey()): ReportDefinitionV1[] => {
  const next = loadReports(tenantKey).filter((item) => item.id !== id)
  persistReports(next, tenantKey)
  return next
}

export const createReportId = (): string => {
  const cryptoObj = (globalThis as any).crypto as Crypto | undefined
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID()
  }

  const rand = Math.random().toString(16).slice(2)
  return `report-${Date.now().toString(16)}-${rand}`
}

export const createReport = (input: {
  id?: string
  name: string
  datasetKey: ReportDatasetKey
  visibility: ReportVisibility
  columns: string[]
  scope?: ReportScopeV1 | null
  fx?: ReportFxConfigV1 | null
  createdAt?: string
  updatedAt?: string
}, tenantKey = getActiveTenantKey()): ReportDefinitionV1 => {
  const now = new Date().toISOString()

  return {
    version: 1,
    id: input.id ?? createReportId(),
    tenantKey,
    name: input.name.trim(),
    datasetKey: input.datasetKey,
    visibility: input.visibility,
    columns: input.columns,
    scope: normalizeReportScope(input.scope) ?? null,
    fx: normalizeReportFx(input.fx) ?? null,
    createdAt: input.createdAt ?? now,
    updatedAt: input.updatedAt ?? now
  }
}

// Exported so `dashboardStorage.ts` can seed Panel widgets against these
// same well-known reports without guessing/duplicating the id strings.
export const LOT_COST_PER_KG_HARVEST_REPORT_ID = 'seed-lot-cost-per-kg-v1'
export const LOT_COST_PER_KG_INFLIGHT_REPORT_ID = 'seed-lot-cost-per-kg-inflight-v1'

// v2.2 T3-B-05d, columns unchanged since: lot key, harvest / feed totals,
// settled FCR, cost totals + currency, cost/kg, category shares, and the
// MIXED-currency triage column. `totalHarvestCount` (T3-B-06b) so
// count-only harvests are visible even when `totalHarvestKg` sums to
// `0.000` and `costPerKgMajor` stays null.
const HARVEST_REPORT_COLUMNS = [
  'lotKey',
  'totalHarvestKg',
  'totalHarvestCount',
  'totalFeedKg',
  'economicFcr',
  'biologicalFcr',
  'feedConversionRatio',
  'totalCostsMajor',
  'currency',
  'costPerKgMajor',
  'sharePctN',
  'sharePctL',
  'sharePctG',
  'sharePctH',
  'sharePctOther',
  'currencyBreakdown'
]

// 2026-09-12 pack: the in-flight companion deliberately omits
// `totalHarvestKg` / `totalHarvestCount` / `costPerKgMajor` /
// `feedConversionRatio` — those are guaranteed-null for exactly the
// unharvested lots this report exists to surface (cost-per-kg has no
// harvest-kg denominator yet), so including them would just show a wall
// of "--" and undercut the naming-clarity fix this pack is for. Adds
// `speciesKey` / `expectedFcrRangeLabel` for context the harvest report
// doesn't need (a settled ratio speaks for itself; an in-progress one
// benefits from knowing the target range) and the in-flight band.
const INFLIGHT_REPORT_COLUMNS = [
  'lotKey',
  'speciesKey',
  'expectedFcrRangeLabel',
  'totalFeedKg',
  'feedEventCount',
  'economicFcr',
  'biologicalFcr',
  'feedConversionRatioInFlight',
  'feedConversionRatioInFlightBand',
  'totalCostsMajor',
  'currency',
  'sharePctN',
  'sharePctL',
  'sharePctG',
  'sharePctH',
  'sharePctOther',
  'currencyBreakdown'
]

const createLotCostPerKgSeed = (
  id: string,
  name: string,
  columns: string[],
  tenantKey: string
): ReportDefinitionV1 => createReport({ id, name, datasetKey: 'lotCostPerKg', visibility: 'private', columns }, tenantKey)

/**
 * v2.2 T3-B-05d, split 2026-09-12: seed the two canonical cost-per-kg
 * report definitions — harvest (settled FCR + cost/kg) and in-flight
 * (mid-cycle FCR, no cost/kg since there's no harvest-kg denominator yet)
 * — on the current tenant. Idempotent by design; never overwrites an
 * operator's edit. Either seeded report is a normal `ReportDefinitionV1`
 * the operator can rename, edit, or delete exactly like any other report.
 *
 * Two paths:
 * - **Empty tenant** (genuinely zero saved reports): seeds both fresh,
 *   correctly named from the start.
 * - **Already-seeded tenant** (pre-2026-09-12 sessions saved the original
 *   single ambiguous "vista general" report): a one-time migration.
 *   Renames the harvest report only if its name still exactly matches
 *   `legacyName` — an operator who already renamed it is left untouched —
 *   then adds the in-flight companion if it isn't already present. A
 *   tenant whose harvest seed was deleted (or never existed — some other
 *   report present instead) is left alone entirely: this function has
 *   never presumed to seed anything beyond the one canonical cost-per-kg
 *   pair, and won't start now.
 *
 * Returns the reports list after seeding/migrating (or the untouched list
 * when neither path applies).
 */
export const seedDefaultReportsIfEmpty = (
  names: { harvest: string; inFlight: string },
  legacyName: string,
  tenantKey = getActiveTenantKey()
): ReportDefinitionV1[] => {
  const existing = loadReports(tenantKey)

  if (existing.length === 0) {
    let next = upsertReport(createLotCostPerKgSeed(LOT_COST_PER_KG_HARVEST_REPORT_ID, names.harvest, HARVEST_REPORT_COLUMNS, tenantKey), tenantKey)
    next = upsertReport(createLotCostPerKgSeed(LOT_COST_PER_KG_INFLIGHT_REPORT_ID, names.inFlight, INFLIGHT_REPORT_COLUMNS, tenantKey), tenantKey)
    return next
  }

  const harvestSeed = existing.find((r) => r.id === LOT_COST_PER_KG_HARVEST_REPORT_ID)
  if (!harvestSeed) return existing

  let next = existing
  if (harvestSeed.name === legacyName) {
    next = upsertReport({ ...harvestSeed, name: names.harvest }, tenantKey)
  }

  const hasInFlight = next.some((r) => r.id === LOT_COST_PER_KG_INFLIGHT_REPORT_ID)
  if (!hasInFlight) {
    next = upsertReport(createLotCostPerKgSeed(LOT_COST_PER_KG_INFLIGHT_REPORT_ID, names.inFlight, INFLIGHT_REPORT_COLUMNS, tenantKey), tenantKey)
  }

  return next
}

// PRD `docs/reports-panel-fcr-sgr-sfr-timeseries-prd.md` item (c). Exported
// so `dashboardStorage.ts` can seed the matching Panel widgets against this
// same well-known report without guessing/duplicating the id string.
export const LOT_GROWTH_PERFORMANCE_REPORT_ID = 'seed-lot-growth-performance-v1'

const LOT_GROWTH_PERFORMANCE_REPORT_COLUMNS = [
  'weekDisplay',
  'lot',
  'sgrPercent',
  'fcrPlanned',
  'sfrPercent'
]

/**
 * PRD `docs/reports-panel-fcr-sgr-sfr-timeseries-prd.md` item (c). Seeds
 * a single report over the new `lotGrowthPerformance` dataset. Unlike
 * `seedDefaultReportsIfEmpty` above, there's no legacy report to migrate
 * from here — this is a plain "create once if the well-known id is
 * missing" seed. Never touches a tenant that already has it, whether the
 * operator kept it as-seeded or renamed/edited it.
 */
export const seedLotGrowthPerformanceReportIfMissing = (
  name: string,
  tenantKey = getActiveTenantKey()
): ReportDefinitionV1[] => {
  const existing = loadReports(tenantKey)
  if (existing.some((r) => r.id === LOT_GROWTH_PERFORMANCE_REPORT_ID)) return existing

  return upsertReport(createReport({
    id: LOT_GROWTH_PERFORMANCE_REPORT_ID,
    name,
    datasetKey: 'lotGrowthPerformance',
    visibility: 'private',
    columns: LOT_GROWTH_PERFORMANCE_REPORT_COLUMNS
  }, tenantKey), tenantKey)
}

// v2.2 findings.md #45: backs the three "core monitoring" Panel widgets
// (mortality rate, stock, average weight over time) — the GAP/ASC/BAP P0
// daily streams not already covered by the FCR/SGR seeds. One report over
// `productionDatatable` rather than `feed`: `feed` drops every row without
// a feed entry, which would silently drop mortality/weight-only days.
// Exported so `dashboardStorage.ts` can seed widgets against it.
export const CORE_MONITORING_REPORT_ID = 'seed-core-monitoring-v1'

const CORE_MONITORING_REPORT_COLUMNS = [
  'date',
  'lot',
  'weight',
  'dailyMortality',
  'mortalityRatePct',
  'fishStockDerived',
  'biomassKgDerived'
]

/**
 * v2.2 findings.md #45. Same plain "create once if the well-known id is
 * missing" seed as `seedLotGrowthPerformanceReportIfMissing` — never
 * touches a tenant that already has it, renamed/edited or not.
 */
export const seedCoreMonitoringReportIfMissing = (
  name: string,
  tenantKey = getActiveTenantKey()
): ReportDefinitionV1[] => {
  const existing = loadReports(tenantKey)
  if (existing.some((r) => r.id === CORE_MONITORING_REPORT_ID)) return existing

  return upsertReport(createReport({
    id: CORE_MONITORING_REPORT_ID,
    name,
    datasetKey: 'productionDatatable',
    visibility: 'private',
    columns: CORE_MONITORING_REPORT_COLUMNS
  }, tenantKey), tenantKey)
}
